import api from 'api/client';
import { ENDPOINTS } from 'api/endpoints';
import {
  AuthPageConfig,
  ContentPage,
  FeatureFlags,
  HeaderStyle,
  NavigationConfig,
  PageLayout,
  ThemeConfig,
  ThemeContactConfig,
  ThemeCookiesConfig,
  ThemeFooterConfig,
  NavItem,
} from 'types/storefront';
import { defaultAuthPageConfig, mapAuthPageResponse } from 'utils/authPage';
import { normalizeDownloadUrl } from 'utils/downloadSection';

const defaultFeatures: FeatureFlags = {
  promotionsEnabled: true,
  brandsEnabled: true,
  hidePricesForAnonymous: false,
  multipleCartsEnabled: false,
  b2bPortalEnabled: true,
  storePaused: false,
};

const asString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
};

const asBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value;
  return undefined;
};

const mapContact = (raw: unknown): ThemeContactConfig | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;
  const data = raw as Record<string, unknown>;
  const contact: ThemeContactConfig = {
    whatsapp: asString(data.whatsapp),
    phone: asString(data.phone),
    email: asString(data.email),
    address: asString(data.address),
    hours: asString(data.hours),
    instagramUrl: asString(data.instagramUrl),
    facebookUrl: asString(data.facebookUrl),
    tiktokUrl: asString(data.tiktokUrl),
    youtubeUrl: asString(data.youtubeUrl),
    xUrl: asString(data.xUrl),
    linkedinUrl: asString(data.linkedinUrl),
    pinterestUrl: asString(data.pinterestUrl),
    telegramUrl: asString(data.telegramUrl),
  };
  return Object.values(contact).some(Boolean) ? contact : undefined;
};

const mapCookies = (raw: unknown): ThemeCookiesConfig | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;
  const data = raw as Record<string, unknown>;
  const cookies: ThemeCookiesConfig = {
    enabled: asBoolean(data.enabled),
    message: asString(data.message),
    acceptLabel: asString(data.acceptLabel),
  };
  if (cookies.enabled === undefined && !cookies.message && !cookies.acceptLabel) {
    return undefined;
  }
  return cookies;
};

const mapFooter = (raw: unknown): ThemeFooterConfig | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;
  const data = raw as Record<string, unknown>;
  const customHtml = typeof data.customHtml === 'string' && data.customHtml.trim() ? data.customHtml : undefined;
  const footer: ThemeFooterConfig = {
    customEnabled: asBoolean(data.customEnabled),
    customHtml,
  };
  if (footer.customEnabled === undefined && !footer.customHtml) return undefined;
  return footer;
};

const mapHeaderStyle = (value: unknown): HeaderStyle | undefined => {
  if (value === 'classic' || value === 'branded') return value;
  return undefined;
};

const parseContentDispositionFileName = (header: string): string | undefined => {
  const utfMatch = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1].trim());
    } catch {
      return utfMatch[1].trim();
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(header);
  if (quoted?.[1]) return quoted[1];
  const plain = /filename=([^;]+)/i.exec(header);
  return plain?.[1]?.trim().replace(/^["']|["']$/g, '') || undefined;
};

export const mapThemeResponse = (data: Record<string, unknown>): ThemeConfig => ({
  logoUrl: asString(data.logoUrl),
  faviconUrl: asString(data.faviconUrl),
  placeholderImageUrl: asString(data.placeholderImageUrl),
  primaryColor: asString(data.primaryColor),
  secondaryColor: asString(data.secondaryColor),
  defaultColorMode: (data.defaultColorMode ?? data.paletteMode) as 'light' | 'dark' | undefined,
  siteName: asString(data.siteName),
  seoDescription: asString(data.seoDescription),
  ogImageUrl: asString(data.ogImageUrl),
  homeHeading: asString(data.homeHeading),
  fontFamily: asString(data.fontFamily),
  headerStyle: mapHeaderStyle(data.headerStyle) ?? 'classic',
  contact: mapContact(data.contact),
  cookies: mapCookies(data.cookies),
  footer: mapFooter(data.footer),
});

const storefrontService = {
  getTheme: async (): Promise<ThemeConfig | null> => {
    try {
      const response = await api.get<Record<string, unknown>>(ENDPOINTS.STOREFRONT_THEME);
      return mapThemeResponse(response.data ?? {});
    } catch {
      return null;
    }
  },

  getLayout: async (page: string): Promise<PageLayout> => {
    const response = await api.get<PageLayout>(ENDPOINTS.STOREFRONT_LAYOUT, { params: { page } });
    return response.data;
  },

  getPage: async (slug: string): Promise<ContentPage> => {
    const { data } = await api.get<ContentPage>(ENDPOINTS.STOREFRONT_PAGE(slug));
    return data;
  },

  getNavigation: async (): Promise<NavigationConfig> => {
    try {
      const response = await api.get<{ header?: Array<Record<string, unknown>>; footer?: Array<Record<string, unknown>> }>(
        ENDPOINTS.STOREFRONT_NAVIGATION,
      );
      const asOrder = (value: unknown): number => {
        if (typeof value === 'number' && Number.isFinite(value)) return value;
        if (typeof value === 'string' && value.trim()) {
          const parsed = Number(value);
          if (Number.isFinite(parsed)) return parsed;
        }
        return 0;
      };
      const isVisible = (item: Record<string, unknown>): boolean =>
        item.visible !== false && item.Visible !== false;
      const mapItems = (items: Array<Record<string, unknown>> = []): NavItem[] =>
        items
          .filter(isVisible)
          .map((item) => ({
            label: String(item.label ?? item.Label ?? '').trim() || undefined,
            labelKey: String(item.labelKey ?? item.label ?? item.Label ?? '').trim() || 'nav.item',
            url: String(item.url ?? item.Url ?? '/'),
            order: asOrder(item.order ?? item.Order),
            visible: true,
            children: item.children || item.Children
              ? mapItems((item.children ?? item.Children) as Array<Record<string, unknown>>)
              : undefined,
          }))
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      return {
        header: mapItems(response.data.header),
        footer: mapItems(response.data.footer),
      };
    } catch {
      return { header: [], footer: [] };
    }
  },

  getFeatures: async (): Promise<FeatureFlags> => {
    try {
      const response = await api.get<FeatureFlags>(ENDPOINTS.STOREFRONT_FEATURES);
      return { ...defaultFeatures, ...response.data };
    } catch {
      return defaultFeatures;
    }
  },

  getAuthPage: async (): Promise<AuthPageConfig> => {
    try {
      const response = await api.get<Record<string, unknown>>(ENDPOINTS.STOREFRONT_AUTH);
      return mapAuthPageResponse(response.data ?? {});
    } catch {
      return defaultAuthPageConfig();
    }
  },

  downloadFile: async (kind: string, fallbackFileName = 'descarga'): Promise<void> => {
    try {
      const response = await api.get<Blob>(ENDPOINTS.STOREFRONT_DOWNLOAD(kind), {
        responseType: 'blob',
      });
      const header = String(response.headers['content-disposition'] ?? '');
      const fileName = parseContentDispositionFileName(header) || fallbackFileName;
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error: unknown) {
      const data = (error as { response?: { data?: unknown } })?.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text()) as { message?: string };
          if (parsed?.message) {
            throw new Error(parsed.message);
          }
        } catch (inner) {
          if (inner instanceof Error && inner !== error && !(inner instanceof SyntaxError)) {
            throw inner;
          }
        }
      }
      throw error;
    }
  },

  resolveDownloadLink: async (kind: string): Promise<string> => {
    const response = await api.get<{ url?: string; Url?: string }>(ENDPOINTS.STOREFRONT_DOWNLOAD(kind));
    const url = normalizeDownloadUrl(response.data?.url ?? response.data?.Url);
    if (!url) {
      throw new Error('El enlace no está disponible.');
    }
    return url;
  },
};

export default storefrontService;
